import { useState, type FormEvent } from "react";

const FORM_ADDR = "https://readdy.ai/api/form/dahgobroh653ivfvnupg";

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    // honeypot check - real users never fill this
    const decoy = String(formData.get("website_alt") ?? "").trim();
    if (decoy) {
      setStatus("success");
      setMessage("提交成功，我们会在人工审核后通过邮件回复你。");
      form.reset();
      return;
    }

    formData.delete("website_alt");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      payload.append(key, String(value));
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "提交失败，请稍后重试。";
      const isSpam = `${serverMsg}`.toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus("success");
        setMessage("提交成功，我们会在人工审核后通过邮件回复你。");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg);
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交未成功，请稍后重试。");
    }
  };

  return (
    <form
      id="contact-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-2">
          <label htmlFor="contact-name" className="text-sm font-medium text-foreground-800">
            你的称呼 <span className="text-primary-500">*</span>
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            maxLength={40}
            placeholder="例如：小张"
            className="rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>
        <div className="flex flex-col gap-2">
          <label htmlFor="contact-email" className="text-sm font-medium text-foreground-800">
            联系邮箱 <span className="text-primary-500">*</span>
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            placeholder="用于回复审核结果"
            className="rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-2">
        <label htmlFor="contact-type" className="text-sm font-medium text-foreground-800">
          素材类型
        </label>
        <select
          id="contact-type"
          name="content_type"
          defaultValue="现场实拍"
          className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
        >
          <option value="现场实拍">现场实拍</option>
          <option value="第一现场">第一现场</option>
          <option value="完整版">完整版</option>
          <option value="现场录像">现场录像</option>
          <option value="路透片段">路透片段</option>
          <option value="分直拍">分直拍</option>
          <option value="其他合作">其他合作</option>
        </select>
      </div>

      <div className="mt-4 flex flex-col gap-2">
        <label htmlFor="contact-source" className="text-sm font-medium text-foreground-800">
          素材来源链接
        </label>
        <input
          id="contact-source"
          name="source_url"
          type="url"
          placeholder="https:// 开头的可访问地址"
          className="rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
        />
      </div>

      <div className="mt-4 flex flex-col gap-2">
        <label htmlFor="contact-detail" className="text-sm font-medium text-foreground-800">
          补充说明 <span className="text-primary-500">*</span>
        </label>
        <textarea
          id="contact-detail"
          name="detail"
          required
          maxLength={500}
          rows={4}
          placeholder="请简单说明视频发生的时间、场景，以及希望收录的分类，最多 500 字。"
          className="resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
        ></textarea>
      </div>

      {/* anti-spam field */}
      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="form-decoy-field"
      />

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-5 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 cursor-pointer transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
      >
        {status === "submitting" ? (
          <>
            <i className="ri-loader-4-line w-4 h-4 flex items-center justify-center animate-spin"></i>
            提交中…
          </>
        ) : (
          <>
            <i className="ri-send-plane-line w-4 h-4 flex items-center justify-center"></i>
            提交素材
          </>
        )}
      </button>

      {status === "success" ? (
        <p className="mt-4 inline-flex items-center gap-2 rounded-md bg-accent-100 px-3.5 py-2.5 text-sm text-accent-950">
          <i className="ri-checkbox-circle-fill w-4 h-4 flex items-center justify-center"></i>
          {message}
        </p>
      ) : null}

      {status === "error" ? (
        <p className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary-100 px-3.5 py-2.5 text-sm text-primary-900">
          <i className="ri-error-warning-line w-4 h-4 flex items-center justify-center"></i>
          {formError}
        </p>
      ) : null}
    </form>
  );
}